import type { CmDeliverable } from '@feature/contract-mgmt/types/contract-mgmt-components';
import type { ProcRow } from '@type/store';
import { findCmRowIndex } from '@lib/procurement';

export function resolveCmStoreRow(
  contractMgmt: Record<string, ProcRow[]>,
  cat: string,
  row?: ProcRow | null,
  index?: number | null,
): { row: ProcRow; index: number } {
  const list = contractMgmt[cat] || [];
  let idx = typeof index === 'number' && index >= 0 ? index : -1;
  if (idx >= 0 && list[idx] && row) {
    const at = list[idx];
    const mismatch = (row.procId && at.procId !== row.procId)
      || (row.procRef && at.procRef !== row.procRef)
      || (!row.procId && !row.procRef && row.name && at.name !== row.name);
    if (mismatch) idx = -1;
  }
  if (idx < 0 && row) idx = findCmRowIndex(list, row);
  const resolved = idx >= 0 ? list[idx] : (row || {});
  const resolvedIdx = idx >= 0 ? idx : findCmRowIndex(list, resolved);
  return { row: resolved, index: resolvedIdx };
}

export const CM_DELIVERABLE_STATUSES = [
  'Pending',
  'Submitted',
  'Under Review',
  'Approved',
  'Paid',
] as const;

export const CM_PAYMENT_STATUSES = [
  'Pending',
  'Partially Paid',
  'Paid',
  '1 IPC Paid',
  '2 IPC Paid',
  '3 IPC Paid',
] as const;

export const CM_INSURANCE_TYPES = [
  'Professional Indemnity',
  'Public Liability',
  'Contractors All Risk',
  'Workmen Compensation',
  'Comprehensive General Liability',
  'Other',
] as const;

export const CM_PERF_SECURITY_TYPES = ['CDR', 'Bank Guarantee'] as const;
export const CM_PERF_SECURITY_STATUSES = ['WithHeld', 'Released'] as const;

export function isCmBankGuaranteeType(type: unknown): boolean {
  const t = String(type || '').trim().toLowerCase();
  return t === 'bank guarantee' || t === 'bank gurantee';
}

export function cmPerfSecurityExpiryAlert(validityDate: unknown): 'expired' | 'expiring' | null {
  if (!validityDate) return null;
  const d = new Date(String(validityDate));
  if (Number.isNaN(d.getTime())) return null;
  const diffDays = (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  if (diffDays < 0) return 'expired';
  if (diffDays <= 30) return 'expiring';
  return null;
}

export function fmtCmInsuranceExpiry(expiryDate: unknown): string {
  const alert = cmPerfSecurityExpiryAlert(expiryDate);
  if (alert === 'expired') return 'Expired';
  if (alert === 'expiring') return 'Renew within 30 days';
  return '';
}

export function fmtCmPerfSecurityExpiry(validityDate: unknown): string {
  return fmtCmInsuranceExpiry(validityDate);
}

export function fmtCmPerfSecurityRemarks(row: Pick<ProcRow, 'perfSecurityType' | 'perfSecurityRemarks'>): string {
  if (!isCmBankGuaranteeType(row.perfSecurityType)) return '—';
  const remarks = String(row.perfSecurityRemarks || '').trim();
  return remarks || '—';
}

export function normalizeCmGoodsRow(row: ProcRow | null | undefined): ProcRow {
  const r: ProcRow = { ...(row || {}) };
  r.perfSecurityType = r.perfSecurityType || '';
  r.perfSecurity = r.perfSecurity || '';
  if (!isCmBankGuaranteeType(r.perfSecurityType)) {
    r.perfSecurityRemarks = '';
    r.perfSecurityValidityDate = '';
    r.perfSecurityIssueDate = '';
  }
  return r;
}

export function emptyCmDeliverable(sr = 1): CmDeliverable {
  return {
    sr,
    name: '',
    timeline: '',
    submissionDate: '',
    actualSubmission: '',
    approvalDate: '',
    amount: '',
    provisionalSum: '',
    contingencyAmount: '',
    paymentStatus: 'Pending',
    status: '',
    remarks: '',
  };
}

export function cmDeliverablePaymentStatus(d: CmDeliverable): string {
  return String(d.paymentStatus || d.payment || 'Pending').trim() || 'Pending';
}

export function normalizeCmDeliverable(d: CmDeliverable, sr = 1): CmDeliverable {
  return {
    ...d,
    sr: d.sr ?? sr,
    paymentStatus: cmDeliverablePaymentStatus(d),
    status: d.status ?? '',
    provisionalSum: d.provisionalSum ?? '',
    contingencyAmount: d.contingencyAmount ?? '',
  };
}

export function normalizeCmDeliverables(list: unknown): CmDeliverable[] {
  if (!Array.isArray(list)) return [];
  return list.map((row, i) => normalizeCmDeliverable(row as CmDeliverable, i + 1));
}

export function cmContractTotalAmount(row: Pick<ProcRow, 'totalRemuneration' | 'totalReimbursable' | 'pkr'> | Record<string, unknown>): number {
  const rem = parseCmMoney((row as ProcRow).totalRemuneration);
  const reimb = parseCmMoney((row as ProcRow).totalReimbursable);
  if (rem || reimb) return rem + reimb;
  return parseCmMoney((row as ProcRow).pkr);
}

export function cmCategoryContractTotal(rows: ProcRow[]): number {
  return (rows || []).reduce((sum, row) => sum + cmContractTotalAmount(row), 0);
}

export function cmContractPaymentLabel(row: ProcRow): string {
  const deliverables = normalizeCmDeliverables(row.deliverables);
  if (deliverables.length) {
    const statuses = deliverables.map(cmDeliverablePaymentStatus);
    if (statuses.every((s) => s === 'Paid')) return 'Paid';
    if (statuses.some((s) => s === 'Paid' || s.includes('IPC Paid'))) {
      const ipc = statuses.filter((s) => s.includes('IPC Paid')).length;
      if (ipc) return `${ipc} IPC Paid`;
      return 'Partially Paid';
    }
    return statuses[0] || 'Pending';
  }
  return String(row.payment || '—');
}

export function normalizeCmSvcRow(row: ProcRow | null | undefined): ProcRow {
  const r: ProcRow = { ...(row || {}) };
  if (!Array.isArray(r.deliverables)) {
    r.deliverables = [];
  }
  r.deliverables = normalizeCmDeliverables(r.deliverables);
  r.insurance = r.insurance || 'Valid';
  r.payment = cmContractPaymentLabel(r);
  r.insuranceType = r.insuranceType || '';
  r.insuranceName = r.insuranceName || '';
  r.insuranceIssuance = r.insuranceIssuance || '';
  r.insuranceExpiry = r.insuranceExpiry || '';
  r.insuranceRemarks = r.insuranceRemarks || '';
  r.paymentPct = r.paymentPct ?? '';
  r.paymentPctRemarks = r.paymentPctRemarks || '';
  r.totalRemuneration = r.totalRemuneration ?? '';
  r.totalReimbursable = r.totalReimbursable ?? '';
  r.paidAmount = r.paidAmount ?? '';
  r.paidRemuneration = r.paidRemuneration ?? '';
  r.paidReimbursable = r.paidReimbursable ?? '';
  r.perfSecurityType = r.perfSecurityType || '';
  r.perfSecurity = r.perfSecurity || '';
  if (!isCmBankGuaranteeType(r.perfSecurityType)) {
    r.perfSecurityRemarks = '';
    r.perfSecurityValidityDate = '';
    r.perfSecurityIssueDate = '';
  }
  return r;
}

export function parseCmMoney(v: unknown): number {
  if (v == null || v === '' || v === '—') return 0;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  return Number(String(v).replace(/,/g, '').trim()) || 0;
}

export function fmtCmSvcMoney(v: unknown): string {
  const n = parseCmMoney(v);
  return n ? `PKR ${n.toLocaleString()}` : '—';
}
